import axios from 'axios'
import parser from 'ua-parser-js'
import AppState from '@/plugins/appstate'
import Logger from '@/plugins/logger'
import {session} from '@/api/store'

// 토큰 갱신 상태 관리
let isRefreshing = false // 토큰 갱신 API 호출 중 여부 (중복 호출 방지)
let failedQueue = [] // 갱신 대기 중인 요청들의 Promise 큐
let isSessionExpired = false // 세션 만료 처리 중복 실행 방지

// 토큰 갱신 완료 후 대기 중인 요청들 일괄 처리
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error) // 갱신 실패 → 대기 요청들도 실패 처리
    } else {
      prom.resolve(token) // 갱신 성공 → 새 토큰 전달하여 재시도
    }
  })
  failedQueue = [] // 큐 초기화
}

// 세션 만료 플래그 리셋 (로그인 성공 시 호출)
const resetSessionExpiredFlag = () => {
  isSessionExpired = false
}

// 세션 만료 시 로그아웃 → 리다이렉트 → 알림 순서로 처리
const handleSessionExpired = async () => {
  if (isSessionExpired) {
    return
  }
  isSessionExpired = true

  // 1. 로그아웃 처리 (동적 import로 순환 참조 방지)
  const {logout} = await import('@/api/access')
  await logout()

  // 2. 로그인 페이지로 이동 (현재 언어 설정 유지)
  if (AppState.App && AppState.App.$router) {
    const currentRoute = AppState.App.$route
    const lang = (currentRoute && currentRoute.params && currentRoute.params.lang) || 'ko'
    // 이미 로그인 페이지면 push 하지 않음 (NavigationDuplicated 방지)
    if (currentRoute && currentRoute.name !== 'login') {
      AppState.App.$router.push({name: 'login', params: {lang}}).catch(() => {})
    }
  }

  // 3. 세션 만료 알림 표시 (로그인 페이지에서 보임)
  if (AppState.App && AppState.App.$awn) {
    AppState.App.$awn.warning(
      AppState.App.$t('error_page.session_expired'),
      {durations: {warning: 3000}},
    )
  }
}

const paymentStatus = process.env.VUE_APP_PAYMENT_STATUS
const base = `${process.env.VUE_APP_API_ADDRESS}/lmm/api/${process.env.VUE_APP_API_VERSION}/`
const serv = {
  launcher: `${process.env.VUE_APP_API_ADDRESS}/launcher/api/${process.env.VUE_APP_API_VERSION}/`,
  upload: `${process.env.VUE_APP_API_ADDRESS}/upload`,
  image: `${process.env.VUE_APP_API_ADDRESS}/images`,
  download: `${process.env.VUE_APP_API_ADDRESS}/download`,
  live: `${process.env.VUE_APP_API_ADDRESS}/live_view`,
  pay: `${process.env.VUE_APP_PAYMENT_ADDRESS}`,
  domain: location.hostname,
  origin: location.origin
}

var _token = null

// Fallback Settings
const timeout = 30000

// Headers
var headers = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
}

// Methods
const SetHeader = function (key, value) {
  if (key === 'token') _token = value
  axios.defaults.headers.common[key] = value
  return axios.defaults.headers.common
}

const SetHeaders = function (keyValueObj) {
  headers = Object.assign({}, headers, keyValueObj)
}

const GetHeader = function (key) {
  return axios.defaults.headers.common[key]
}

const GetHeaders = function () {
  return headers
}

const removeHeader = function (key) {
  delete axios.defaults.headers.common[key]
  return axios.defaults.headers.common
}

const CreateInstance = function (endpoint, rebase) {
  let apiBase = rebase ? serv[rebase] : base
  var instance = axios.create({
    baseURL: apiBase + endpoint,
    timeout: timeout,
    headers: headers,
    withCredentials: false
  })

  const ua = parser()

  instance.interceptors.request.use(
    config => {
      if (/IE/i.test(ua.browser.name)) {
        if (!config.params) config.params = {}
        config.params.cache = new Date().getTime()
      }
      if (_token && !config.headers.token) {
        config.headers.token = _token
      }
      return config
    }
  )

  instance.interceptors.response.use(null,
    async err => {
      const originalRequest = err.config // 실패한 원래 요청 정보

      // 401 에러 && 아직 재시도 안 한 경우
      if (err.response && err.response.status === 401 && !originalRequest._retry) {

        // refresh API 자체가 401이면 → 더 이상 방법 없음 → 로그아웃
        if (originalRequest.url && originalRequest.url.endsWith('/refresh')) {
          await handleSessionExpired()
          return Promise.reject(err)
        }

        // 이미 다른 요청이 refresh 중이면 → 대기열에 추가하고 기다림
        if (isRefreshing) {
          originalRequest.headers.token = await new Promise((resolve, reject) => {
            failedQueue.push({resolve, reject})
          })
          return axios(originalRequest)
        }

        // 첫 번째 access token 401 → refresh 담당
        originalRequest._retry = true // 재시도 표시 (무한루프 방지)
        isRefreshing = true // 다른 요청들은 대기하라고 알림

        try {
          const refreshToken = session.get('refresh_token')
          if (!refreshToken) {
            throw new Error('No refresh token')
          }

          // /refresh API 호출
          const response = await axios.post(
            `${base}refresh`,
            {refresh_token: refreshToken},
            {headers: {'Content-Type': 'application/json'}},
          )

          // 새 토큰 저장
          const {token, refresh_token} = response.data.objects
          session.set('token', token)
          session.set('refresh_token', refresh_token)
          _token = token
          SetHeader('token', token)

          // 대기 중인 요청들에게 새 토큰 전달
          processQueue(null, token)

          // 새 토큰으로 원래 요청 진행
          originalRequest.headers.token = token
          return axios(originalRequest)
        } catch (refreshError) {
          // refresh 실패 → 대기 중인 요청들도 실패 처리 후 로그아웃
          processQueue(refreshError, null)
          await handleSessionExpired()
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false // refresh 끝났음 표시
        }
      }

      // 기존 에러 처리 로직
      let requestData = {}
      if (err.config && err.config.data) {
        try {
          requestData = JSON.parse(err.config.data)
        } catch (e) {
          requestData = err.config.data
        }
      }

      // 요청 데이터에 `export_id`가 존재하면 Interceptor에서 처리하지 않음
      if (requestData && "export_id" in requestData) {
        return Promise.reject(err)
      }

      if (err.response) {
        // 서버에러
        Logger.error(err.response)
        if (AppState.App && AppState.App.$awn) {
          // 403 권한 오류: FastAPI가 보낸 detail 메시지 표시
          if (err.response.status === 403 && err.response.data && err.response.data.detail) {
            AppState.App.$awn.warning(err.response.data.detail)
          } else {
            AppState.App.$awn.alert(AppState.App.$t('response_error_server_error'))
          }
        }
        return Promise.reject(err)
      } else {
        // 타임아웃 혹은 커넥션 실패
        Logger.error(err.code)
        if (AppState.App && AppState.App.$awn) {
          AppState.App.$awn.alert(AppState.App.$t('response_error_connect_failed'))
        }
        return Promise.reject(err)
      }
    }
  )
  return instance
}

export { SetHeader, SetHeaders, GetHeader, GetHeaders, removeHeader, CreateInstance, base, serv, paymentStatus, resetSessionExpiredFlag }
